import { useCallback, useEffect, useMemo, useState } from "react";
import {
  InfiniteListBase,
  RecordRepresentation,
  ShowBase,
  useDataProvider,
  useShowContext,
  useTranslate,
} from "ra-core";
import type { ShowBaseProps } from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Calendar, MessageSquare, Phone, Sparkles } from "lucide-react";
import { Pencil } from "lucide-react";
import { Link } from "react-router";

import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { NoteCreate, NotesIteratorMobile } from "../notes";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TagsListEdit } from "./TagsListEdit";
import { ContactEditSheet } from "./ContactEditSheet";
import { ContactStatusSelector } from "./ContactInputs";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { ContactTasksList } from "./ContactTasksList";
import type { Contact } from "../types";
import { Avatar } from "./Avatar";
import { ContactAside } from "./ContactAside";
import { MobileBackButton } from "../misc/MobileBackButton";
import { RelativeDate } from "../misc/RelativeDate";
import {
  buildLeadSummary,
  computeLeadScore,
  hasPendingInboundWhatsApp,
  leadScoreBadgeClass,
  sortTimelineItemsDesc,
  suggestNextAction,
  type ContactTimelineItem,
  type ContactTimelineKind,
  type LeadAssistInput,
} from "./contactModel";
import type { CallLog, SiteVisit, WhatsAppMessage } from "@/types/realEstate";
import type { ContactNote } from "../types";
import { ContactTelephonyDrawer } from "./ContactTelephonyDrawer";
import { ScheduleVisitDrawer } from "../site-visits/ScheduleVisitDrawer";
import { getSupabaseClient } from "../providers/supabase/supabase";

const TIMELINE_KIND_LABELS: Record<ContactTimelineKind, string> = {
  note: "Note",
  call: "Call",
  whatsapp: "WhatsApp",
  visit: "Visit",
  portal: "Portal",
};

function buildLeadAssistInput(
  contact: Contact,
  timeline: ContactTimelineItem[],
): LeadAssistInput {
  const hasCompletedVisit = timeline.some(
    (item) => item.kind === "visit" && item.meta === "Completed",
  );
  const hasUpcomingVisit = timeline.some(
    (item) =>
      item.kind === "visit" &&
      (item.meta === "Scheduled" || item.meta === "Reschedule"),
  );
  const lastInboundWhatsAppBody = timeline.find(
    (item) => item.kind === "whatsapp" && item.meta === "INBOUND",
  )?.body;
  const needsWhatsAppReply = hasPendingInboundWhatsApp(timeline);

  return {
    contact,
    timeline,
    hasCompletedVisit,
    hasUpcomingVisit,
    lastInboundWhatsAppBody,
    needsWhatsAppReply,
  };
}

function useContactTimeline(contactId?: string | number) {
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [items, setItems] = useState<ContactTimelineItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (contactId == null) return;

    let cancelled = false;
    const listParams = {
      filter: { contact_id: contactId },
      pagination: { page: 1, perPage: 50 },
    };

    const loadTimeline = (opts?: { silent?: boolean }) => {
      const silent = opts?.silent === true;
      if (!silent) setLoading(true);

      Promise.all([
        dataProvider.getList<ContactNote>("contact_notes", {
          ...listParams,
          sort: { field: "date", order: "DESC" },
        }),
        dataProvider.getList<CallLog>("call_logs", {
          ...listParams,
          sort: { field: "created_at", order: "DESC" },
        }),
        dataProvider.getList<WhatsAppMessage>("whatsapp_messages", {
          ...listParams,
          sort: { field: "created_at", order: "DESC" },
        }),
        dataProvider.getList<SiteVisit>("site_visits", {
          ...listParams,
          sort: { field: "scheduled_at", order: "DESC" },
        }),
        dataProvider.getList<{
          id: string;
          portal?: string;
          parse_ok?: boolean;
          error_message?: string;
          created_at?: string;
        }>("inbound_leads", {
          ...listParams,
          sort: { field: "created_at", order: "DESC" },
        }),
      ])
        .then(([notes, calls, whatsapp, visits, portals]) => {
          if (cancelled) return;

          const mapped: ContactTimelineItem[] = [
            ...notes.data.map(
              (n): ContactTimelineItem => ({
                id: `note-${n.id}`,
                kind: "note",
                at: n.date,
                title: translate("resources.notes.name", { smart_count: 1 }),
                body: n.text,
                meta: n.status,
              }),
            ),
            ...calls.data.map(
              (c): ContactTimelineItem => ({
                id: `call-${c.id}`,
                kind: "call",
                at: c.created_at,
                title: "Call",
                body: c.call_disposition,
                meta: c.call_duration_seconds
                  ? `${c.call_duration_seconds}s`
                  : undefined,
              }),
            ),
            ...whatsapp.data
              .filter(
                (m) =>
                  m.status !== "campaign" &&
                  !m.message_body?.startsWith("[Campaign]"),
              )
              .map(
                (m): ContactTimelineItem => ({
                  id: `wa-${m.id}`,
                  kind: "whatsapp",
                  at: m.created_at,
                  title: "WhatsApp",
                  body: m.message_body,
                  meta: m.direction,
                  status: m.status,
                }),
              ),
            ...visits.data.map(
              (v): ContactTimelineItem => ({
                id: `visit-${v.id}`,
                kind: "visit",
                at: v.scheduled_at || v.created_at,
                title: "Site visit",
                body: v.feedback_notes,
                meta: v.status,
              }),
            ),
            ...portals.data.map(
              (p): ContactTimelineItem => ({
                id: `portal-${p.id}`,
                kind: "portal",
                at: p.created_at || new Date().toISOString(),
                title: p.portal || "Portal lead",
                body: p.error_message || undefined,
                meta: p.parse_ok ? "OK" : "Failed",
              }),
            ),
          ];

          setItems(sortTimelineItemsDesc(mapped));
        })
        .catch(() => {
          if (!cancelled && !silent) setItems([]);
        })
        .finally(() => {
          if (!cancelled && !silent) setLoading(false);
        });
    };

    loadTimeline();

    const onFocus = () => loadTimeline({ silent: true });
    window.addEventListener("focus", onFocus);

    const supabase = getSupabaseClient();
    const channel = supabase
      .channel(`contact-timeline-${contactId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "contact_notes",
          filter: `contact_id=eq.${contactId}`,
        },
        () => loadTimeline({ silent: true }),
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "call_logs",
          filter: `contact_id=eq.${contactId}`,
        },
        () => loadTimeline({ silent: true }),
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "whatsapp_messages",
          filter: `contact_id=eq.${contactId}`,
        },
        () => loadTimeline({ silent: true }),
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "site_visits",
          filter: `contact_id=eq.${contactId}`,
        },
        () => loadTimeline({ silent: true }),
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "inbound_leads",
          filter: `contact_id=eq.${contactId}`,
        },
        () => loadTimeline({ silent: true }),
      )
      .subscribe();

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      supabase.removeChannel(channel);
    };
  }, [contactId, dataProvider, translate]);

  return { items, loading };
}

const ContactTimelineList = ({
  items,
  loading,
}: {
  items: ContactTimelineItem[];
  loading: boolean;
}) => {
  const translate = useTranslate();

  if (loading) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        {translate("ra.page.loading", { _: "Loading..." })}
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        {translate("crm.common.no_activity", { _: "No activity yet." })}
      </p>
    );
  }

  return (
    <ul className="mt-4 space-y-3">
      {items.map((item) => (
        <li
          key={item.id}
          className="rounded-md border px-3 py-2 text-sm space-y-1"
        >
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{TIMELINE_KIND_LABELS[item.kind]}</Badge>
            <span className="text-xs text-muted-foreground">
              <RelativeDate date={item.at} />
            </span>
            {item.meta && (
              <span className="text-xs text-muted-foreground">{item.meta}</span>
            )}
          </div>
          <p className="font-medium">{item.title}</p>
          {item.body && (
            <p className="text-muted-foreground whitespace-pre-wrap">
              {item.body}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
};

type ContactLeadAssistStripProps = {
  contact: Contact;
  timeline: ContactTimelineItem[];
  onOpenTelephony: (tab: "call" | "whatsapp") => void;
  onOpenScheduleVisit: () => void;
};

const ContactLeadAssistStrip = ({
  contact,
  timeline,
  onOpenTelephony,
  onOpenScheduleVisit,
}: ContactLeadAssistStripProps) => {
  const translate = useTranslate();

  const assistInput = useMemo(
    () => buildLeadAssistInput(contact, timeline),
    [contact, timeline],
  );
  const summary = useMemo(() => buildLeadSummary(assistInput), [assistInput]);
  const score = useMemo(() => computeLeadScore(assistInput), [assistInput]);
  const nextAction = useMemo(
    () => suggestNextAction(assistInput),
    [assistInput],
  );

  const handleAction = useCallback(() => {
    switch (nextAction.action) {
      case "call":
        onOpenTelephony("call");
        break;
      case "whatsapp":
        onOpenTelephony("whatsapp");
        break;
      case "schedule_visit":
        onOpenScheduleVisit();
        break;
      case "none":
      default:
        onOpenTelephony("whatsapp");
        break;
    }
  }, [nextAction.action, onOpenTelephony, onOpenScheduleVisit]);

  return (
    <div className="rounded-lg border bg-muted/30 px-3 py-3 space-y-2 mb-4">
      <div className="flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
        <p className="text-sm text-foreground leading-snug flex-1">{summary}</p>
        <Badge
          variant="secondary"
          className={`shrink-0 text-[10px] px-1.5 py-0 font-medium ${leadScoreBadgeClass(score.tier)}`}
          title={translate("resources.contacts.assist.lead_score")}
        >
          {translate(`resources.contacts.assist.score_${score.tier}`, {
            _: score.tier,
          })}{" "}
          {score.score}
        </Badge>
      </div>
      <div className="flex flex-wrap items-center gap-2 pl-6">
        <span className="text-xs text-muted-foreground">
          {translate("resources.contacts.assist.next_action")}:
        </span>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-7 text-xs gap-1"
          onClick={handleAction}
        >
          {nextAction.action === "call" && <Phone className="w-3 h-3" />}
          {(nextAction.action === "whatsapp" ||
            nextAction.action === "none") && (
            <MessageSquare className="w-3 h-3" />
          )}
          {nextAction.action === "schedule_visit" && (
            <Calendar className="w-3 h-3" />
          )}
          {nextAction.label}
        </Button>
        <span className="text-xs text-muted-foreground">
          {nextAction.reason}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 pl-6 pt-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          onClick={() => onOpenTelephony("call")}
        >
          <Phone className="w-3 h-3" />
          {translate("resources.contacts.assist.call")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1 border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
          onClick={() => onOpenTelephony("whatsapp")}
        >
          <MessageSquare className="w-3 h-3" />
          {translate("resources.contacts.assist.whatsapp")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          onClick={onOpenScheduleVisit}
        >
          <Calendar className="w-3 h-3" />
          {translate("resources.contacts.assist.schedule_visit")}
        </Button>
      </div>
    </div>
  );
};

const ContactTimelineSection = ({
  showAssist = true,
  telephonyOpen,
  setTelephonyOpen,
  visitDrawerOpen,
  setVisitDrawerOpen,
  telephonyInitialTab,
  setTelephonyInitialTab,
}: {
  showAssist?: boolean;
  telephonyOpen: boolean;
  setTelephonyOpen: (open: boolean) => void;
  visitDrawerOpen: boolean;
  setVisitDrawerOpen: (open: boolean) => void;
  telephonyInitialTab: "call" | "whatsapp";
  setTelephonyInitialTab: (tab: "call" | "whatsapp") => void;
}) => {
  const { record } = useShowContext<Contact>();
  const { items, loading } = useContactTimeline(record?.id);

  const openTelephony = useCallback(
    (tab: "call" | "whatsapp") => {
      setTelephonyInitialTab(tab);
      setTelephonyOpen(true);
    },
    [setTelephonyInitialTab, setTelephonyOpen],
  );

  if (!record) return null;

  const contactName =
    `${record.first_name || ""} ${record.last_name || ""}`.trim() || "Lead";
  const contactPhone =
    record.phone_jsonb?.[0]?.number || record.phone_secondary || "";

  return (
    <>
      {showAssist && !loading && (
        <ContactLeadAssistStrip
          contact={record}
          timeline={items}
          onOpenTelephony={openTelephony}
          onOpenScheduleVisit={() => setVisitDrawerOpen(true)}
        />
      )}
      <ContactTimelineList items={items} loading={loading} />
      <ContactTelephonyDrawer
        open={telephonyOpen}
        onOpenChange={setTelephonyOpen}
        contactName={contactName}
        contactPhone={contactPhone}
        contactId={record.id}
        contact={record}
        timeline={items}
        initialTab={telephonyInitialTab}
      />
      <ScheduleVisitDrawer
        isOpen={visitDrawerOpen}
        onOpenChange={setVisitDrawerOpen}
        contactId={String(record.id)}
        contactName={contactName}
        contactPhone={contactPhone}
      />
    </>
  );
};

export const ContactShow = (props: ShowBaseProps = {}) => {
  const isMobile = useIsMobile();

  return (
    <ShowBase
      queryOptions={{
        onError: isMobile
          ? () => {
              {
                /** Disable error notification as the content handles offline */
              }
            }
          : undefined,
      }}
      {...props}
    >
      {isMobile ? <ContactShowContentMobile /> : <ContactShowContent />}
    </ShowBase>
  );
};

const ContactShowContentMobile = () => {
  const translate = useTranslate();
  const { defaultTitle, record, isPending } = useShowContext<Contact>();
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [telephonyOpen, setTelephonyOpen] = useState(false);
  const [visitDrawerOpen, setVisitDrawerOpen] = useState(false);
  const [telephonyInitialTab, setTelephonyInitialTab] = useState<
    "call" | "whatsapp"
  >("whatsapp");

  if (isPending || !record) return null;

  const taskCount = record.nb_tasks ?? 0;

  return (
    <>
      {/* We need to repeat the note creation sheet here to support the note 
      create button that is rendered when there are no notes. */}
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={record.id}
      />
      <ContactEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        contactId={record.id}
      />
      <MobileHeader>
        <MobileBackButton />
        <div className="flex flex-1 min-w-0">
          <Link to="/contacts" className="flex-1 min-w-0">
            <h1 className="truncate text-xl font-semibold">{defaultTitle}</h1>
          </Link>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1 text-emerald-700 shrink-0"
          onClick={() => {
            setTelephonyInitialTab("whatsapp");
            setTelephonyOpen(true);
          }}
        >
          <MessageSquare className="size-4" />
          {translate("resources.contacts.assist.engage")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label={translate("ra.action.edit")}
          onClick={() => setEditOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </MobileHeader>
      <MobileContent>
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <Avatar />
            <div className="mx-3 flex-1">
              <h2 className="text-2xl font-bold">
                <RecordRepresentation />
              </h2>
              <div className="text-sm text-muted-foreground">
                {record.title && record.company_id != null
                  ? `${translate("resources.contacts.position_at", {
                      title: record.title,
                    })} `
                  : record.title}
                {record.company_id != null && (
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link="show"
                  >
                    <TextField source="name" className="underline" />
                  </ReferenceField>
                )}
              </div>
            </div>
            <div>
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
                className="no-underline"
              >
                <CompanyAvatar />
              </ReferenceField>
            </div>
          </div>
        </div>

        <Tabs defaultValue="timeline" className="w-full">
          <TabsList className="grid w-full grid-cols-4 h-10">
            <TabsTrigger value="timeline">
              {translate("crm.common.timeline", { _: "Timeline" })}
            </TabsTrigger>
            <TabsTrigger value="notes">
              {translate("resources.notes.name", { smart_count: 2 })}
            </TabsTrigger>
            <TabsTrigger value="tasks">
              {translate("crm.common.task_count", {
                smart_count: taskCount ?? 0,
              })}
            </TabsTrigger>
            <TabsTrigger value="details">
              {translate("crm.common.details")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="mt-2">
            <ContactTimelineSection
              telephonyOpen={telephonyOpen}
              setTelephonyOpen={setTelephonyOpen}
              visitDrawerOpen={visitDrawerOpen}
              setVisitDrawerOpen={setVisitDrawerOpen}
              telephonyInitialTab={telephonyInitialTab}
              setTelephonyInitialTab={setTelephonyInitialTab}
            />
          </TabsContent>

          <TabsContent value="notes" className="mt-2">
            <InfiniteListBase
              resource="contact_notes"
              filter={{ contact_id: record.id }}
              sort={{ field: "date", order: "DESC" }}
              perPage={25}
              disableSyncWithLocation
              storeKey={false}
              empty={
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <p className="text-muted-foreground mb-4">
                    {translate("resources.notes.empty")}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setNoteCreateOpen(true)}
                  >
                    {translate("resources.notes.action.add")}
                  </Button>
                </div>
              }
              loading={false}
              error={false}
              queryOptions={{
                onError: () => {
                  /** override to hide notification as error case is handled by NotesIteratorMobile */
                },
              }}
            >
              <NotesIteratorMobile contactId={record.id} showStatus />
            </InfiniteListBase>
          </TabsContent>

          <TabsContent value="tasks" className="mt-4">
            <ContactTasksList />
          </TabsContent>

          <TabsContent value="details" className="mt-4">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold">
                  {translate("resources.notes.fields.status")}
                </h3>
                <Separator />
                <div className="mt-3">
                  <ContactStatusSelector />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {translate(
                    "resources.contacts.field_categories.personal_info",
                  )}
                </h3>
                <Separator />
                <div className="mt-3">
                  <ContactPersonalInfo />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {translate(
                    "resources.contacts.field_categories.background_info",
                  )}
                </h3>
                <Separator />
                <div className="mt-3">
                  <ContactBackgroundInfo />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {translate("resources.tags.name", { smart_count: 2 })}
                </h3>
                <Separator />
                <div className="mt-3">
                  <TagsListEdit />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </MobileContent>
    </>
  );
};

const ContactShowContent = () => {
  const translate = useTranslate();
  const { record, isPending } = useShowContext<Contact>();
  const [telephonyOpen, setTelephonyOpen] = useState(false);
  const [visitDrawerOpen, setVisitDrawerOpen] = useState(false);
  const [telephonyInitialTab, setTelephonyInitialTab] = useState<
    "call" | "whatsapp"
  >("whatsapp");

  if (isPending || !record) return null;

  return (
    <div className="mt-2 mb-2 flex gap-8">
      <div className="flex-1">
        <Card>
          <CardContent>
            <div className="flex">
              <Avatar />
              <div className="ml-2 flex-1">
                <h5 className="text-xl font-semibold">
                  <RecordRepresentation />
                </h5>
                <div className="inline-flex text-sm text-muted-foreground">
                  {record.title && record.company_id != null
                    ? `${translate("resources.contacts.position_at", {
                        title: record.title,
                      })} `
                    : record.title}
                  {record.company_id != null && (
                    <ReferenceField
                      source="company_id"
                      reference="companies"
                      link="show"
                    >
                      &nbsp;
                      <TextField source="name" />
                    </ReferenceField>
                  )}
                </div>
              </div>
              <div>
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link="show"
                  className="no-underline"
                >
                  <CompanyAvatar />
                </ReferenceField>
              </div>
            </div>
            <NoteCreate reference="contacts" showStatus className="mt-4" />
            <ContactTimelineSection
              telephonyOpen={telephonyOpen}
              setTelephonyOpen={setTelephonyOpen}
              visitDrawerOpen={visitDrawerOpen}
              setVisitDrawerOpen={setVisitDrawerOpen}
              telephonyInitialTab={telephonyInitialTab}
              setTelephonyInitialTab={setTelephonyInitialTab}
            />
          </CardContent>
        </Card>
      </div>
      <ContactAside />
    </div>
  );
};
